import { FC, useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Widget, WidgetEmptyState, WidgetInboxList } from '@factorialco/f0-react/dist/experimental'
import { NewInboxHomeWidgetDocument, NewInboxHomeWidgetQuery } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

import { NEW_HOME_WIDGETS, NEW_HOME_WIDGET_ACTION_TYPES } from 'modules/dashboard/lib/events'
import { getModule } from 'modules/inbox_tasks/activities/Task/utils'
import {
  getTaskDescription,
  getTaskListItemAffecteeInfo,
  getTaskTitle,
  useTaskListItemUrl,
} from 'modules/inbox_tasks/lib/helpers'

import './index.styles.css'
const useLinkAndTitle = () => {
  const title = i18n.t('new_dashboard.inbox.title')

  const navigate = useNavigate()

  const { navigation } = useNavigation()

  const linkUrl = navigation.inbox.todo.fullPath()

  const handleActionClick = () => {
    track(trackingEvents.dashboard.clickOnHomeWidgetAction, {
      widget: NEW_HOME_WIDGETS.INBOX,
      actionType: NEW_HOME_WIDGET_ACTION_TYPES.BOTTOM_BUTTON,
    })
    navigate(linkUrl)
  }

  const handleUpperRightActionClick = () => {
    track(trackingEvents.dashboard.clickOnHomeWidgetAction, {
      widget: NEW_HOME_WIDGETS.INBOX,
      actionType: NEW_HOME_WIDGET_ACTION_TYPES.UPPER_RIGHT_BUTTON,
    })
  }

  const link = {
    url: linkUrl,
    title,
    onClick: handleUpperRightActionClick,
  }

  return {
    title,
    link,
    handleActionClick,
  }
}

const prepareData = (data: NewInboxHomeWidgetQuery) => {
  const count = data.tasks.inboxTasksConnection.totalCount ?? 0

  const items = data.tasks.inboxTasksConnection.nodes

  return {
    count,
    items,
  }
}

type PreparedData = ReturnType<typeof prepareData>

const InboxWidgetComponent: FC<{ data: PreparedData; employeeId: number }> = ({
  data: { items, count },
}) => {
  const { link, title, handleActionClick } = useLinkAndTitle()

  const { getTaskListItemUrl } = useTaskListItemUrl()

  const normalizedItems = useMemo(
    () =>
      items
        .map((item) => {
          const { fullName: affecteeName } = getTaskListItemAffecteeInfo(item)

          const module = getModule(item.category ?? undefined)

          const itemTitle = getTaskTitle({
            taskName: item.name,
            taskableInfoForTitle: item.taskable,
            affecteeName,
            groupedCount: item.groupedTasksConnection?.totalCount ?? 0,
            shortVersion: true,
            dueOn: item.dueOn ?? undefined,
          })

          let description = getTaskDescription({
            taskableInfoForDescription: item.taskable,
          })

          description = [
            convertDate(item.createdAt, 'backend_datetime', 'ui_dmy_long'),
            ...(description ?? []),
          ]

          if (!itemTitle || item.status === 'done') return

          return {
            id: item.id,
            title: itemTitle,
            module: module?.id,
            subtitle: description.join(' · '),
            url: getTaskListItemUrl(item),
          }
        })
        .filter(Boolean),
    [items, getTaskListItemUrl]
  )

  const [visibleItemsCount, setVisibleItemsCount] = useState(0)

  const handleVisibleItemsChange = useCallback((visibleItems: unknown[]) => {
    setVisibleItemsCount(visibleItems.length)
  }, [])

  const remainingItemsCount = count - visibleItemsCount

  const buttonLabel =
    remainingItemsCount > 0
      ? i18n.tp('new_dashboard.inbox.see_more', { count: remainingItemsCount })
      : undefined

  const navigate = useNavigate()

  const handleClickItem = (itemId: number | string) => {
    const item = normalizedItems.find((it) => it.id === itemId)

    if (!item) return

    const isExternalUrl = ['http://', 'https://'].some((pattern) => item.url.startsWith(pattern))

    if (isExternalUrl) {
      window.open(item.url, '_blank')
      return
    }

    navigate(item.url)
  }

  return (
    <Widget
      header={{
        title,
        link,
        count,
      }}
      action={buttonLabel ? { label: buttonLabel, onClick: handleActionClick } : undefined}
    >
      <Box className={'InboxWidget_container'}>
        <WidgetInboxList
          items={normalizedItems}
          onClickItem={handleClickItem}
          onVisibleItemsChange={handleVisibleItemsChange}
        />
      </Box>
    </Widget>
  )
}

const EmptyComponent = () => {
  const { link, title } = useLinkAndTitle()

  return (
    <Widget header={{ title, link }}>
      <WidgetEmptyState
        title={i18n.t('new_dashboard.inbox.empty.title')}
        description={i18n.t('new_dashboard.inbox.empty.description')}
        emoji="☕️"
      />
    </Widget>
  )
}

export const buildInboxWidget = () =>
  buildWidget({
    id: 'inbox',
    document: NewInboxHomeWidgetDocument,
    emptyComponent: () => <EmptyComponent />,
    isEmpty: ({ items }) => !items.length,
    prepareData,
    component: InboxWidgetComponent,
  })
